// 마음건강 분석 결과 저장소
import { MentalHealthReport } from '@/util/bcg/types';

export interface StoredMentalHealthReport {
  id: number | string; // sessionId(string) 또는 로컬 ID(number)
  userId: number;
  userName: string;
  filename: string;
  analysisDate: string;
  report: MentalHealthReport;
}

class MentalHealthStore {
  private storageKey = 'mental_health_reports';

  // 분석 결과 저장
  saveReport(filename: string, report: MentalHealthReport, userId?: number, userName?: string, customId?: string): StoredMentalHealthReport {
    const reports = this.getAllReports();
    
    // customId가 있으면 사용 (sessionId), 없으면 자동 생성
    const reportId = customId || `local_${Math.max(0, ...reports.map(r => typeof r.id === 'number' ? r.id : 0)) + 1}`;
    
    const storedReport: StoredMentalHealthReport = {
      id: reportId as any,
      userId: userId || 0,
      userName: userName || '알 수 없음',
      filename,
      analysisDate: new Date().toISOString(),
      report
    };

    reports.unshift(storedReport); // 최신 순으로 정렬
    this.saveToStorage(reports);
    
    return storedReport;
  }

  // 모든 분석 결과 조회
  getAllReports(): StoredMentalHealthReport[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('분석 결과 로드 실패:', error);
      return [];
    }
  }

  // 특정 분석 결과 조회
  getReportById(id: number | string): StoredMentalHealthReport | null {
    const reports = this.getAllReports();
    return reports.find(r => r.id === id || r.id.toString() === id.toString()) || null;
  }

  // 분석 결과 삭제
  deleteReport(id: number | string): boolean {
    const reports = this.getAllReports();
    const filteredReports = reports.filter(r => r.id !== id && r.id.toString() !== id.toString());
    
    if (filteredReports.length !== reports.length) {
      this.saveToStorage(filteredReports);
      return true;
    }
    return false;
  }

  // 모든 분석 결과 삭제
  clearAllReports(): void {
    localStorage.removeItem(this.storageKey);
  }

  // 대시보드 통계 계산
  getDashboardStats() {
    const reports = this.getAllReports();
    
    if (reports.length === 0) {
      return {
        totalUsers: 0,
        recentAnalyses: 0,
        averageMindScore: 0,
        riskUsers: 0,
        attentionUsers: 0,
        normalUsers: 0,
        healthyUsers: 0
      };
    }

    // 최근 30일 분석
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentReports = reports.filter(r => new Date(r.analysisDate) >= thirtyDaysAgo);

    // 상태별 분류
    const statusCounts = {
      healthy: 0,
      normal: 0,
      caution: 0,
      risk: 0
    };

    reports.forEach(report => {
      const label = report.report.decisionLabel;
      switch (label) {
        case '건강':
          statusCounts.healthy++;
          break;
        case '보통':
          statusCounts.normal++;
          break;
        case '주의':
          statusCounts.caution++;
          break;
        case '위험':
          statusCounts.risk++;
          break;
      }
    });

    // 평균 점수 계산
    const totalScore = reports.reduce((sum, r) => sum + r.report.mindScore.score, 0);
    const averageScore = reports.length > 0 ? totalScore / reports.length : 0;

    return {
      totalUsers: new Set(reports.map(r => r.userId)).size, // 고유 사용자 수
      recentAnalyses: recentReports.length,
      averageMindScore: averageScore,
      riskUsers: statusCounts.risk,
      attentionUsers: statusCounts.caution,
      normalUsers: statusCounts.normal,
      healthyUsers: statusCounts.healthy
    };
  }

  // 추이 데이터 계산 (최근 30일)
  getTrendData() {
    const reports = this.getAllReports();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const trendData: Array<{
      date: string;
      averageScore: number;
      analysisCount: number;
    }> = [];

    // 최근 30일 각 날짜별 데이터 생성
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayReports = reports.filter(r => {
        const reportDate = new Date(r.analysisDate).toISOString().split('T')[0];
        return reportDate === dateStr;
      });

      const averageScore = dayReports.length > 0
        ? dayReports.reduce((sum, r) => sum + r.report.mindScore.score, 0) / dayReports.length
        : 0;

      trendData.push({
        date: dateStr,
        averageScore,
        analysisCount: dayReports.length
      });
    }

    return trendData;
  }

  // 최근 분석 결과 (최대 10개)
  getRecentAnalyses(limit: number = 10) {
    return this.getAllReports()
      .slice(0, limit)
      .map(stored => ({
        id: stored.id,
        userId: stored.userId,
        userName: stored.userName,
        analysisDate: stored.analysisDate,
        mindScore: stored.report.mindScore.score,
        decisionLabel: stored.report.decisionLabel,
        stressCategory: stored.report.stressAnalysis.category
      }));
  }

  private saveToStorage(reports: StoredMentalHealthReport[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(reports));
    } catch (error) {
      console.error('분석 결과 저장 실패:', error);
    }
  }
}

export const mentalHealthStore = new MentalHealthStore();
